import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, formatApiError } from "@/context/AuthContext";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";

function PasswordInput({ value, onChange, testId, minLength }) {
    const [show, setShow] = useState(false);
    return (
        <div className="relative">
            <input
                type={show ? "text" : "password"}
                required
                minLength={minLength}
                className="input-line pr-10"
                value={value}
                onChange={onChange}
                data-testid={testId}
                autoComplete={minLength ? "new-password" : "current-password"}
            />
            <button
                type="button"
                onClick={() => setShow((s) => !s)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--sb-text-muted)] hover:text-[var(--sb-brand)] p-1"
                aria-label={show ? "Hide password" : "Show password"}
                data-testid={`${testId}-toggle`}
                tabIndex={-1}
            >
                {show ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
        </div>
    );
}

export function Login() {
    const [form, setForm] = useState({ email: "", password: "" });
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const { login } = useAuth();
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const next = params.get("next") || "/dashboard";

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true); setError("");
        try {
            await login(form.email, form.password);
            toast.success("Welcome back.");
            navigate(next);
        } catch (err) {
            setError(formatApiError(err.response?.data?.detail) || err.message);
        } finally {
            setBusy(false);
        }
    };

    return (
        <AuthShell title="Sign in" subtitle="Welcome back.">
            <form onSubmit={submit} className="space-y-6" data-testid="login-form">
                <div>
                    <label className="overline block mb-2">Email</label>
                    <input type="email" required className="input-line" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} data-testid="login-email" />
                </div>
                <div>
                    <label className="overline block mb-2">Password</label>
                    <PasswordInput value={form.password} onChange={(e) => setForm({...form, password: e.target.value})} testId="login-password" />
                </div>
                {error && <div className="text-sm text-red-400" data-testid="login-error">{error}</div>}
                <button disabled={busy} className="btn-primary w-full justify-center" data-testid="login-submit">{busy ? "…" : "Sign in"}</button>
                <p className="text-sm text-[var(--sb-text-dim)] text-center">
                    New here? <Link to="/register" className="text-[var(--sb-brand)] hover:text-[var(--sb-text)]">Create an account</Link>
                </p>
            </form>
        </AuthShell>
    );
}

export function Register() {
    const [form, setForm] = useState({ name: "", email: "", password: "" });
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const { register } = useAuth();
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const next = params.get("next") || "/dashboard";

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true); setError("");
        try {
            await register(form.name, form.email, form.password);
            toast.success("Welcome aboard.");
            navigate(next);
        } catch (err) {
            setError(formatApiError(err.response?.data?.detail) || err.message);
        } finally {
            setBusy(false);
        }
    };

    return (
        <AuthShell title="Create account" subtitle="Join the flight log.">
            <form onSubmit={submit} className="space-y-6" data-testid="register-form">
                <div>
                    <label className="overline block mb-2">Your name</label>
                    <input required className="input-line" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} data-testid="register-name" />
                </div>
                <div>
                    <label className="overline block mb-2">Email</label>
                    <input type="email" required className="input-line" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} data-testid="register-email" />
                </div>
                <div>
                    <label className="overline block mb-2">Password</label>
                    <PasswordInput value={form.password} onChange={(e) => setForm({...form, password: e.target.value})} testId="register-password" minLength={6} />
                    <p className="text-xs text-[var(--sb-text-muted)] mt-2">Six characters minimum.</p>
                </div>
                {error && <div className="text-sm text-red-400" data-testid="register-error">{error}</div>}
                <button disabled={busy} className="btn-primary w-full justify-center" data-testid="register-submit">{busy ? "…" : "Create account"}</button>
                <p className="text-sm text-[var(--sb-text-dim)] text-center">
                    Already on board? <Link to="/login" className="text-[var(--sb-brand)] hover:text-[var(--sb-text)]">Sign in</Link>
                </p>
            </form>
        </AuthShell>
    );
}

function AuthShell({ title, subtitle, children }) {
    return (
        <div className="min-h-[80vh] grid grid-cols-1 lg:grid-cols-2">
            <div className="hidden lg:block relative">
                <img src="https://images.pexels.com/photos/29353711/pexels-photo-29353711.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=900" alt="Loch" className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[var(--sb-bg)]" />
                <div className="absolute bottom-12 left-12 right-12">
                    <div className="overline mb-3 text-[var(--sb-text)]">DTScotslad</div>
                    <p className="font-serif text-3xl text-[var(--sb-text)] tracking-tight leading-tight max-w-md">
                        &ldquo;The weather wins about half the time. That&rsquo;s why we keep coming back.&rdquo;
                    </p>
                </div>
            </div>
            <div className="flex items-center justify-center px-6 py-20 lg:px-16">
                <div className="w-full max-w-md">
                    <div className="overline mb-3">{title}</div>
                    <h1 className="font-serif text-4xl lg:text-5xl tracking-tighter leading-none mb-10">{subtitle}</h1>
                    {children}
                </div>
            </div>
        </div>
    );
}
